import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
} from '@mui/material';
import { min, max, mean, startCase, get } from 'lodash';

import { formatCurrencyFloat, formatCurrencyInt } from 'shared/utils/numberFormatters';
import { divide } from 'shared/utils/numberOperations';
import { EntryMode, ExpenseComp, ExpenseForecast, ExpenseHistory } from 'types/underwriting/income-approach/expenses';
import { expenseForecastFormHelpers } from './expenseForecastFormConfig';
import NumericInput from 'components/shared/inputs/NumericInput';
import { BASIS_TYPES, DEFAULT_EXPENSES_KEYS } from 'shared/constants/expenses';
import { useRootStore } from 'stores/RootStore';
import { ExpenseCategory } from '../types';
import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';

interface ExpenseForecastCardProps {
  category: ExpenseCategory;
  expenseHistory: ExpenseHistory[];
  expenseComps: ExpenseComp[];
  expenseForecast: ExpenseForecast;
  form: any;
  underwritingData: UnderwritingOutput;
}

const SLIDER_WIDTH = 700;

const getSliderPosition = (value: number, minValue: number, maxValue: number) => {
  if (maxValue === minValue) return 0;
  return ((value - minValue) / (maxValue - minValue)) * 100;
};

const ExpenseForecastCard: React.FC<ExpenseForecastCardProps> = ({
  category,
  expenseHistory,
  expenseComps,
  form,
  expenseForecast,
  underwritingData,
}) => {
  const { underwritingPageStore } = useRootStore();
  const { key, category: categoryName, isCustom = false } = category;

  const { property } = underwritingPageStore.underwritingData || {};
  const { residentialUnits = 0, grossBuildingArea = 0 } = property || {};
  const expenseHistoryEntryMode: EntryMode | undefined = get(
    underwritingData,
    'incomeApproach.expenses.expenseHistoryEntryMode'
  );

  const effectiveGrossIncome = useMemo(
    () =>
      incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, INCOME_APPROACH_BASIS_TYPES.projected),
    [underwritingData]
  );

  const subjectActuals =
    expenseHistoryEntryMode !== EXPENSE_SUMMARIZED_ENTRY_MODE
      ? expenseHistory.map((history: any) => {
          const expense = (history.expenses || []).find((e: any) => e.category === categoryName);
          const total = expense ? expense.value : 0;
          const perUnit = divide(total, residentialUnits);
          const psf = divide(total, grossBuildingArea);
          return {
            year: history.year,
            period: startCase(history.period),
            total,
            perUnit,
            psf,
          };
        })
      : [];

  const basis = expenseForecast?.basis || BASIS_TYPES.psf;
  const basisToUseForComparison = basis === BASIS_TYPES.percentOfEGI ? BASIS_TYPES.total : basis;
  const compStats = expenseComps
    .map((comp: ExpenseComp) => {
      // @ts-ignore
      const compExpense = comp.expenses[key];
      return compExpense ? compExpense[basisToUseForComparison] : null;
    })
    .filter((v: any) => v !== null && v !== undefined);

  const compMin = compStats.length ? min(compStats) : null;
  const compAvg = compStats.length ? mean(compStats) : null;
  const compMax = compStats.length ? max(compStats) : null;

  const concludedValue = expenseForecastFormHelpers.getConcludedValueByBasis(
    expenseForecast?.concludedValue || 0,
    basis,
    grossBuildingArea,
    residentialUnits,
    effectiveGrossIncome
  );
  const forecastValue = get(concludedValue, basis);

  const sliderValues = [
    ...subjectActuals.map((actual) => get(actual, basisToUseForComparison)),
    compMin,
    compAvg,
    compMax,
    forecastValue,
  ];
  const sliderMin = sliderValues.length ? min(sliderValues) : 0;
  const sliderMax = sliderValues.length ? max(sliderValues) : 1;

  const renderSlider = (markers: any[], label: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" align="center" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ position: 'relative', height: 60, width: SLIDER_WIDTH, mx: 'auto' }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '100%',
            height: 4,
            bgcolor: '#b3e0ff',
            borderRadius: 2,
            transform: 'translateY(-50%)',
          }}
        />
        {markers.map(
          (marker: any, idx: number) =>
            marker.value !== null && (
              <Box
                key={`${key}-${idx}-${marker.label}`}
                sx={{
                  position: 'absolute',
                  left: `calc(${getSliderPosition(marker.value, sliderMin, sliderMax)}% - 50px)`,
                  top: isCustom ? 20 : -5,
                  width: 100,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: marker.color, fontWeight: 600 }}>
                  {formatCurrencyFloat(marker.value)}
                </Typography>
                <Box
                  sx={{
                    width: marker.size || 12,
                    height: marker.size || 12,
                    bgcolor: marker.color,
                    borderRadius: '50%',
                    mx: 'auto',
                    mt: 0.5,
                  }}
                />
                <Typography variant="caption" sx={{ color: marker.color }}>
                  {marker.label}
                </Typography>
              </Box>
            )
        )}
      </Box>
    </Box>
  );

  const handleConcludedValueChange = (value: number) => {
    expenseForecastFormHelpers.updateExpenseForecast(form, key, 'concludedValue', value);
  };

  const handleBasisChange = (event: any) => {
    const value = event.target.value;
    expenseForecastFormHelpers.updateExpenseForecast(form, key, 'basis', value);
  };
  const isManagementFees = key === DEFAULT_EXPENSES_KEYS.managementFees;
  const showPercentage = isManagementFees && basis === 'percentOfEGI';

  return (
    <Card sx={{ mb: 2 }} key={`${key}-forecast-card`}>
      <CardContent>
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: 18 }} gutterBottom>
          {categoryName}
        </Typography>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item xs={4}>
            <RadioGroup value={basis} onChange={handleBasisChange} row>
              <FormControlLabel value="psf" control={<Radio />} label="Per SF" />
              <FormControlLabel value="perUnit" control={<Radio />} label="Per Unit" />
              <FormControlLabel value="total" control={<Radio />} label="Per Year" />
              {isManagementFees && <FormControlLabel value="percentOfEGI" control={<Radio />} label="% of EGI" />}
            </RadioGroup>
            <Box sx={{ mt: 2 }}>
              {subjectActuals.map((expenseHistory) => {
                const { year, period } = expenseHistory;
                const value = get(expenseHistory, basisToUseForComparison);
                return (
                  <Typography variant="body2">
                    <span style={{ fontWeight: 'bold' }}>{`${year} ${startCase(period)}`}</span>:{' '}
                    {value !== null ? formatCurrencyInt(value) : '-'}
                  </Typography>
                );
              })}
              <Typography variant="body2">
                <span style={{ fontWeight: 'bold' }}>Comp Min</span>:{' '}
                {compMin !== null ? formatCurrencyInt(compMin) : '-'}
              </Typography>
              <Typography variant="body2">
                <span style={{ fontWeight: 'bold' }}>Comp Avg</span>:{' '}
                {compAvg !== null ? formatCurrencyInt(compAvg) : '-'}
              </Typography>
              <Typography variant="body2">
                <span style={{ fontWeight: 'bold' }}>Comp Max</span>:{' '}
                {compMax !== null ? formatCurrencyInt(compMax) : '-'}
              </Typography>
              <Box sx={{ mt: 2, mb: 2 }}>
                <NumericInput
                  value={expenseForecast?.concludedValue || 0}
                  onChange={(value: any) => handleConcludedValueChange(value)}
                  size="small"
                  margin="normal"
                  label="Appraiser's Forecast"
                  fullWidth
                  decimalScale={basis === BASIS_TYPES.psf ? 2 : 0}
                  InputProps={{
                    startAdornment: !showPercentage && <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: showPercentage && <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
                {isManagementFees && (
                  <Typography variant="caption">
                    Effective Gross Income: {formatCurrencyInt(effectiveGrossIncome)}
                  </Typography>
                )}
              </Box>
              {basis !== BASIS_TYPES.psf && (
                <Typography variant="body2">
                  Per SF: {concludedValue.psf ? formatCurrencyFloat(concludedValue.psf) : '-'}
                </Typography>
              )}
              {basis !== BASIS_TYPES.perUnit && (
                <Typography variant="body2">
                  Per Unit: {concludedValue.perUnit ? formatCurrencyInt(concludedValue.perUnit) : '-'}
                </Typography>
              )}
              {basis !== BASIS_TYPES.total && (
                <Typography variant="body2">
                  Per Year: {concludedValue.total ? formatCurrencyInt(concludedValue.total) : '-'}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={8}>
            {subjectActuals.map((actual, idx) =>
              renderSlider(
                get(actual, basisToUseForComparison) !== null
                  ? [
                      {
                        value: get(actual, basisToUseForComparison),
                        color: '#1976d2',
                        label: `${actual.year} ${actual.period}`,
                      },
                    ]
                  : [],
                `${actual.year} ${actual.period}`
              )
            )}

            {renderSlider(
              [
                compMin !== null ? { value: compMin, color: '#d32f2f', label: 'Min' } : null,
                compAvg !== null ? { value: compAvg, color: '#d32f2f', label: 'Avg' } : null,
                compMax !== null ? { value: compMax, color: '#d32f2f', label: 'Max' } : null,
              ].filter(Boolean),
              'Comparable Range'
            )}

            {renderSlider(
              get(concludedValue, basisToUseForComparison) !== null
                ? [
                    {
                      value: get(concludedValue, basisToUseForComparison),
                      color: '#388e3c',
                      label: "Appraiser's Forecast",
                      size: 14,
                    },
                  ]
                : [],
              "Appraiser's Forecast"
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ExpenseForecastCard;
